import { ReactNode, useEffect, useRef, useState } from 'react'
import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface InfoPopoverProps {
  /** Short heading, shown uppercase in Messaging Advisory blue. */
  title: string
  /** Body — usually a few short lines of "what it is / when to use it". */
  children: ReactNode
  /**
   * Optional visual example. Left as a slot so per-element graphics can be
   * dropped in later without touching this component or its call sites.
   */
  imageSlot?: ReactNode
  /** Which edge of the trigger the panel aligns to (avoids clipping at screen edges). */
  align?: 'left' | 'right'
  triggerClassName?: string
  ariaLabel?: string
}

/**
 * Branded (Messaging Advisory blue) click-to-open help popover for CONCEPTS —
 * "what is this element / template / brand and when do I use it". Unlike Tooltip
 * it holds multi-line content and an optional example image, opens on click (so
 * it works on touch), and closes on outside-click or Escape.
 *
 * Designed to sit inside other clickable UI (e.g. a selectable card): the trigger
 * and panel both stopPropagation so opening help never triggers the parent's
 * click. Render it as a SIBLING of the parent button, not a child — a button
 * nested in a button is invalid HTML.
 */
export function InfoPopover({
  title,
  children,
  imageSlot,
  align = 'right',
  triggerClassName = '',
  ariaLabel,
}: InfoPopoverProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-label={ariaLabel || 'More info'}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          setOpen((prev) => !prev)
        }}
        className={`inline-flex items-center justify-center rounded-full text-primary-500 transition-colors hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300 ${triggerClassName}`}
      >
        <QuestionMarkCircleIcon className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="dialog"
          onClick={(event) => event.stopPropagation()}
          className={`absolute top-6 z-50 w-64 rounded-xl border border-primary-100 bg-white p-3 text-left shadow-xl ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <p className="text-xs font-bold uppercase tracking-wide text-primary-700">{title}</p>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                setOpen(false)
              }}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          {imageSlot && (
            <div className="mb-2 overflow-hidden rounded-lg border border-gray-100 bg-gray-50">{imageSlot}</div>
          )}
          <div className="space-y-1.5 text-[11px] leading-5 text-gray-600">{children}</div>
        </div>
      )}
    </div>
  )
}
